'use client'

import articlePlaceholder from '@/assets/images/placeholder/articlePlaceholder.svg'
import useOnMouseAndScroll from '@/shared/hooks/useOnMouseAndScroll'
import { PlaceholderValue } from 'next/dist/shared/lib/get-img-props'
import Image, { StaticImageData } from 'next/image'
import { useEffect, useRef, useState } from 'react'

import articleLight from '@/assets/images/placeholder/lightMode/article_placeholder.svg'
import playerLight from '@/assets/images/placeholder/lightMode/player_placeholder.svg'
import seriesLight from '@/assets/images/placeholder/lightMode/series_placeholder.svg'
import teamLight from '@/assets/images/placeholder/lightMode/team_placeholder.svg'

import articleDark from '@/assets/images/placeholder/articlePlaceholder.svg'
import playerDark from '@/assets/images/placeholder/darkMode/player.png'
import seriesDark from '@/assets/images/placeholder/darkMode/series_placeholder.svg'
import teamDark from '@/assets/images/placeholder/darkMode/team.png'

type MyImageProps = {
  src: string | StaticImageData
  className?: string
  placeholderImage?: string | StaticImageData
  height?: number | `${number}`
  width?: number | `${number}`
  loadOnScroll?: boolean
  alt?: string
  sizes?: string
  placeholder?: PlaceholderValue
  blurDataURL?: string
  placeholderType?: 'team' | 'article' | 'players' | 'series'
}

function MyImage({
  src,
  placeholderImage,
  loadOnScroll,
  className,
  alt,
  height,
  width,
  placeholderType = 'article',
  ...rest
}: MyImageProps) {
  const { isLoaded } = useOnMouseAndScroll()
  const wasError = useRef(false)

  const [isDark, setDark] = useState(true)
  const [url, setUrl] = useState<string | StaticImageData>(src)

  const getThemeBasedPlaceholder = () => {
    if (placeholderImage) return placeholderImage

    const map = {
      article: isDark ? articleDark : articleLight,
      players: isDark ? playerDark : playerLight,
      team: isDark ? teamDark : teamLight,
      series: isDark ? seriesDark : seriesLight
    }

    return map[placeholderType] || articlePlaceholder
  }

  // Track theme changes
  useEffect(() => {
    const updateTheme = () => {
      const lightModeAttr = document.documentElement.getAttribute('data-light-mode')
      setDark(lightModeAttr === 'false')
    }

    const observer = new MutationObserver(updateTheme)
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['data-light-mode']
    })

    updateTheme()
    return () => observer.disconnect()
  }, [])

  // On image source or scroll trigger
  useEffect(() => {
    if (loadOnScroll && !isLoaded) {
      setUrl(getThemeBasedPlaceholder())
    } else if (!wasError.current) {
      setUrl(src)
    }
  }, [src, isLoaded])

  // On theme toggle, force placeholder update only if it’s not yet loaded or error occurred
  useEffect(() => {
    if ((loadOnScroll && !isLoaded) || wasError.current) {
      setUrl(getThemeBasedPlaceholder())
    }
  }, [isDark])

  // Debug
  if (placeholderType === 'players') {
    const logUrl = typeof url === 'string' ? url : url?.src
    const logPlaceholder =
      typeof getThemeBasedPlaceholder() === 'string'
        ? getThemeBasedPlaceholder()
        : getThemeBasedPlaceholder()?.src
    console.log(isDark ? 'Dark mode' : 'Light mode', logUrl, 'url', 'placeholder:', logPlaceholder)
  }

  return (
    <Image
      key={typeof url === 'string' ? url : url?.src}
      className={className}
      src={url}
      height={height}
      width={width}
      quality={50}
      alt={alt || ''}
      onError={() => {
        wasError.current = true
        setUrl(getThemeBasedPlaceholder())
      }}
      unoptimized
      {...rest}
    />
  )
}

export default MyImage
